// Calendario del shell «Orion»: reúne en una sola lista por día las citas de
// la plataforma — reservas 1:1 (mcp_django) y la agenda del agente (Orión) —
// y los eventos del Google Calendar de la persona (si lo conectó).
// Un evento que la plataforma creó en Google no se duplica.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { CalendarDays, ExternalLink, Link2, Unlink } from 'lucide-react';

import { useModules } from '@/context/ModulesContext';
import { canOpen } from '../nav';
import { DAY_MS, isActiveAppt, makeWindow, useAppointments, type Appt } from '../data';
import {
  currentReturnUrl, disconnectGcal, gcalTime, getConnectUrl, useGcalStatus, useMyGcalEvents,
  type GcalEvent,
} from '../googleCalendar';
import { Button, Card, EmptyState, ErrorNote, Loading, PageHeader } from '../ui';

const HORIZON_DAYS = 45;
const SAME_START_MS = 60_000;

const fmtDay = new Intl.DateTimeFormat('es-MX', { weekday: 'long', day: 'numeric', month: 'long' });
const fmtTime = new Intl.DateTimeFormat('es-MX', { hour: 'numeric', minute: '2-digit' });

type Kind = 'google' | 'agente' | 'reservas';

interface Item {
  key: string;
  title: string;
  start: number;
  end: number | null;
  allDay: boolean;
  kind: Kind;
  status?: string;
  link?: string;
  location?: string;
  /** cita de la plataforma que también está en Google */
  inGoogle?: boolean;
}

const KIND_LABEL: Record<Kind, string> = { google: 'Google', agente: 'Agente', reservas: 'Reserva' };

function statusLabel(s?: string): string {
  const k = String(s || '').toLowerCase();
  if (k === 'confirmed') return 'Confirmada';
  if (k === 'pending' || k === 'pending_confirmation') return 'Por confirmar';
  if (k === 'completed' || k === 'done') return 'Realizada';
  return s || 'Programada';
}

/** Une citas + eventos de Google sin duplicar los que la plataforma creó. */
function mergeItems(appts: Appt[], events: GcalEvent[]): Item[] {
  const items: Item[] = appts.map((a) => ({
    key: a.id,
    title: a.title,
    start: a.start,
    end: a.end,
    allDay: false,
    kind: a.source,
    status: a.status,
  }));
  for (const e of events) {
    const start = gcalTime(e.inicio);
    if (start === null) continue;
    if (e.de_la_plataforma) {
      const twin = items.find(
        (i) =>
          i.kind !== 'google' &&
          ((e.booking_id != null && i.key === `b-${e.booking_id}`) || Math.abs(i.start - start) < SAME_START_MS),
      );
      if (twin) {
        twin.inGoogle = true;
        twin.link = twin.link || e.link || undefined;
        continue;
      }
    }
    items.push({
      key: `g-${e.id}`,
      title: e.titulo || '(sin título)',
      start,
      end: gcalTime(e.fin),
      allDay: e.todo_el_dia,
      kind: 'google',
      link: e.link || undefined,
      location: e.ubicacion || undefined,
    });
  }
  return items.sort((a, b) => a.start - b.start || Number(b.allDay) - Number(a.allDay));
}

export function OrionCalendarPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const { availableModules, isAdmin, isDev } = useModules();

  const w = useMemo(() => makeWindow(30), []);
  const appts = useAppointments(w);
  const gStatus = useGcalStatus();
  const gConnected = gStatus.data?.kind === 'active';
  const gEvents = useMyGcalEvents(gConnected, HORIZON_DAYS);

  // Aviso al volver de Google (?calendar=ok|error) y limpieza de la query.
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  useEffect(() => {
    const r = params.get('calendar');
    if (r !== 'ok' && r !== 'error') return;
    setNotice(
      r === 'ok'
        ? { ok: true, text: 'Tu Google Calendar quedó conectado.' }
        : { ok: false, text: 'No se pudo conectar tu Google Calendar. Intenta de nuevo.' },
    );
    const next = new URLSearchParams(params);
    next.delete('calendar');
    next.delete('motivo');
    setParams(next, { replace: true });
    void qc.invalidateQueries({ queryKey: ['orion-shell', 'gcal'] });
  }, [params, setParams, qc]);

  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const connect = async () => {
    setBusy(true);
    setActionError(null);
    try {
      window.location.assign(await getConnectUrl(currentReturnUrl()));
    } catch (err) {
      setActionError((err as Error)?.message || 'No se pudo iniciar la conexión con Google.');
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    setActionError(null);
    try {
      await disconnectGcal();
      setConfirming(false);
      setNotice({ ok: true, text: 'Tu Google Calendar se desconectó.' });
      await qc.invalidateQueries({ queryKey: ['orion-shell', 'gcal'] });
    } catch (err) {
      setActionError((err as Error)?.message || 'No se pudo desconectar.');
    } finally {
      setBusy(false);
    }
  };

  const now = Date.now();
  const limit = now + HORIZON_DAYS * DAY_MS;
  const gList = gConnected && gEvents.data?.kind === 'ok' ? gEvents.data.eventos : [];
  const platform = (appts.data?.items ?? []).filter((a) => isActiveAppt(a) && a.start >= now && a.start <= limit);
  const items = mergeItems(platform, gList).filter((i) => (i.end ?? i.start) >= now && i.start <= limit);

  // Agrupa por día civil local; lo que empezó antes (p. ej. de varios días) va en hoy.
  const today = new Date(now);
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const groups: Array<[number, Item[]]> = [];
  for (const it of items) {
    const sd = new Date(Math.max(it.start, todayStart));
    const day = new Date(sd.getFullYear(), sd.getMonth(), sd.getDate()).getTime();
    const last = groups[groups.length - 1];
    if (last && last[0] === day) last[1].push(it);
    else groups.push([day, [it]]);
  }

  const canBookings = canOpen('bookings-1to1/list', { availableModules, isAdmin, isDev });
  const gReconnect = gStatus.data?.kind === 'reconnect' || gEvents.data?.kind === 'reconnect';

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        title="Calendario"
        subtitle="Próximos 45 días en un solo lugar: las citas que agenda tu agente, las reservas de tus clientes y tu Google Calendar."
        actions={
          canBookings && (
            <Button variant="secondary" onClick={() => navigate('/m/bookings-1to1/list')}>
              Administrar reservas
            </Button>
          )
        }
      />

      {notice && (
        <div
          role="status"
          className={`flex items-start gap-3 rounded-xl px-4 py-3 text-sm ${
            notice.ok ? 'bg-primary/10 text-foreground' : 'bg-destructive/10 text-destructive'
          }`}
        >
          <p className="flex-1">{notice.text}</p>
          <button type="button" className="text-xs font-medium underline" onClick={() => setNotice(null)}>
            Cerrar
          </button>
        </div>
      )}

      {gStatus.data?.kind !== 'unavailable' && (
        <Card title="Google Calendar" titleId="gcal-title">
          {gStatus.isLoading ? (
            <Loading label="Revisando tu conexión…" />
          ) : gStatus.isError ? (
            <ErrorNote>No se pudo leer el estado de tu Google Calendar.</ErrorNote>
          ) : (
            <div className="space-y-3">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <p className="min-w-0 flex-1 text-sm text-muted-foreground">
                  {gStatus.data?.kind === 'active' && (
                    <>
                      Conectado como <span className="font-medium text-foreground">{gStatus.data.email || 'tu cuenta de Google'}</span>.
                      Tus eventos aparecen abajo junto a tus citas.
                    </>
                  )}
                  {gReconnect && gStatus.data?.kind !== 'none' && (
                    <span className="text-destructive">
                      Tu Google Calendar requiere reconectarse: la autorización expiró o fue revocada.
                    </span>
                  )}
                  {gStatus.data?.kind === 'none' && 'No conectado. Conéctalo para ver tus eventos de Google junto a tus citas.'}
                </p>
                <div className="flex shrink-0 flex-wrap gap-2">
                  {(gStatus.data?.kind === 'none' || gReconnect) && (
                    <Button variant="primary" onClick={connect} disabled={busy}>
                      <Link2 size={14} aria-hidden /> {gReconnect ? 'Reconectar' : 'Conectar'}
                    </Button>
                  )}
                  {(gStatus.data?.kind === 'active' || gStatus.data?.kind === 'reconnect') && !confirming && (
                    <Button variant="danger" onClick={() => setConfirming(true)} disabled={busy}>
                      <Unlink size={14} aria-hidden /> Desconectar
                    </Button>
                  )}
                </div>
              </div>
              {confirming && (
                <div className="flex flex-col gap-2 rounded-xl border border-border bg-muted/50 px-3 py-3 sm:flex-row sm:items-center">
                  <p className="flex-1 text-sm text-foreground">
                    ¿Desconectar tu Google Calendar? Tu agente dejará de ver tu disponibilidad.
                  </p>
                  <div className="flex gap-2">
                    <Button variant="ghost" onClick={() => setConfirming(false)} disabled={busy}>
                      Cancelar
                    </Button>
                    <Button variant="danger" onClick={disconnect} disabled={busy}>
                      Sí, desconectar
                    </Button>
                  </div>
                </div>
              )}
              {actionError && <ErrorNote>{actionError}</ErrorNote>}
              {gConnected && gEvents.isLoading && <Loading label="Cargando tus eventos de Google…" />}
              {gConnected && gEvents.isError && <ErrorNote>No se pudieron leer tus eventos de Google.</ErrorNote>}
            </div>
          )}
        </Card>
      )}

      <Card title="Próximas citas" titleId="cal-title">
        {appts.isLoading ? (
          <Loading />
        ) : appts.isError && !gList.length ? (
          <ErrorNote>No se pudieron leer las citas.</ErrorNote>
        ) : groups.length === 0 ? (
          <EmptyState icon={<CalendarDays size={18} />} title="Aún no hay citas próximas">
            Cuando tu agente agende citas por WhatsApp o tu sitio, recibas reservas o tengas eventos en tu Google
            Calendar, aparecerán aquí ordenados por día.
          </EmptyState>
        ) : (
          <div className="space-y-5">
            {appts.isError && <ErrorNote>No se pudieron leer las citas de la plataforma; se muestran solo las de Google.</ErrorNote>}
            {groups.map(([day, list]) => (
              <section key={day} aria-label={fmtDay.format(new Date(day))}>
                <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {fmtDay.format(new Date(day))}
                </h3>
                <ul className="divide-y divide-border">
                  {list.map((it) => (
                    <Row key={it.key} it={it} />
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function Row({ it }: { it: Item }) {
  const d = new Date(it.start);
  const google = it.kind === 'google';
  const when = it.allDay ? 'Todo el día' : fmtTime.format(d);
  return (
    <li className="flex items-center gap-3 rounded-xl px-1 py-2">
      <div className="flex h-11 w-11 shrink-0 flex-col items-center justify-center rounded-xl bg-muted text-foreground">
        <span className="text-[10px] font-medium uppercase leading-none text-muted-foreground">
          {d.toLocaleDateString('es-MX', { month: 'short' }).replace('.', '')}
        </span>
        <span className="text-base font-semibold leading-tight tabular-nums">{d.getDate()}</span>
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{it.title}</p>
        <p className="truncate text-xs text-muted-foreground">
          {when}
          {it.location ? ` · ${it.location}` : ''}
          {!google && ` · ${statusLabel(it.status)}`}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        <span
          className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
            google ? 'bg-primary/10 text-primary' : 'bg-muted text-foreground'
          }`}
        >
          {KIND_LABEL[it.kind]}
        </span>
        {it.inGoogle && (
          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">En Google</span>
        )}
        {it.link && (
          <a
            href={it.link}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Abrir en Google Calendar"
            className="rounded-lg p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <ExternalLink size={14} aria-hidden />
          </a>
        )}
      </div>
    </li>
  );
}

export default OrionCalendarPage;
